import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';

export default function Gallery({ items }) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  if (!items || items.length === 0) return null;

  const displayedItems = isExpanded ? items.slice(0, 12) : items.slice(0, 3);

  return (
    <section id="gallery" className="py-16 sm:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-black text-black tracking-tight mb-4 uppercase">
            Galleri
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Glimt fra vores arbejde, kulturudveksling og det stærke fællesskab vi bygger sammen.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedItems.map((item, index) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="relative group overflow-hidden rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-500 cursor-zoom-in aspect-[4/5]"
              onClick={() => setSelectedItem(item)}
            >
              <img
                src={item.image[0]}
                alt={item.alt || item.title?.da || 'Galleri billede'}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                <p className="text-white font-bold text-sm uppercase tracking-wider">
                  {item.category?.da || item.category}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <Dialog.Root open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/90 z-50 backdrop-blur-sm animate-in fade-in duration-300" />
            <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[95vw] max-w-5xl max-h-[90vh] outline-none animate-in zoom-in-95 duration-300">
              <div className="relative w-full h-full flex flex-col items-center">
                <Dialog.Close className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors p-2 outline-none">
                  <X size={32} />
                </Dialog.Close>
                
                {selectedItem && (
                  <div className="w-full flex flex-col items-center">
                    <img
                      src={selectedItem.image[0]}
                      alt={selectedItem.alt || selectedItem.title?.da || 'Galleri billede'}
                      className="max-w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl"
                    />
                    <div className="mt-6 text-center text-white max-w-2xl">
                      <Dialog.Title className="text-2xl font-bold mb-2">
                        {selectedItem.title?.da}
                      </Dialog.Title>
                      <p className="text-red-500 font-bold uppercase tracking-widest text-sm mb-4">
                        {selectedItem.category?.da || selectedItem.category}
                      </p>
                      {selectedItem.description?.da && (
                        <Dialog.Description className="text-gray-300 text-lg">
                          {selectedItem.description.da}
                        </Dialog.Description>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        <div className="mt-12 flex flex-col items-center gap-6">
          {items.length > 3 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-black font-black uppercase tracking-widest hover:text-red-600 transition-colors duration-300 flex items-center gap-2"
            >
              {isExpanded ? 'Vis færre' : 'Vis flere billeder'}
              <motion.span
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                ↓
              </motion.span>
            </button>
          )}
          
          <Link
            to="/galleri"
            className="inline-flex items-center gap-2 px-8 py-4 bg-red-600 text-white font-bold uppercase tracking-widest rounded-full hover:bg-black transition-all duration-300 group hover:-translate-y-1"
          >
            Se alle billeder
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}
